/**
 * @file Document-level reactive wrapper for RxDB documents.
 *
 * Memory model: each wrapper holds only a $raw pointer (RxDocument) plus an
 * optional $modified overlay (null until the user writes a field or calls a
 * method on a nested object). Field reads fall through to $raw when no overlay
 * is set, so unedited documents pay no per-field memory cost beyond their
 * RxDB-side storage.
 *
 * @since 2025-10-19
 */

import {
  validateSchemaValue,
  createDefaultDocument,
} from '../../json-editor/schemas.js';

const { ref, reactive, computed, shallowRef, watch, markRaw } = Vue;

// Global cache for document instances using WeakRef so wrappers can be
// garbage-collected when nothing external references them.
const _documentCache = new Map();

/**
 * Reactive document wrapper.
 *
 * @typedef {object} DocumentInstance
 * @property {object} $schema   Collection schema definition.
 * @property {object|null} $original Read-only snapshot of the persisted data.
 * @property {object} $data     Schema-filtered view of current data (modified + raw).
 * @property {boolean} $isModified True when local state differs from persisted state.
 * @property {boolean} $isValid True when $data passes schema validation.
 * @property {boolean} $isLoading True during async operations / before raw is bound.
 * @property {boolean} $isNew   True for unsaved documents.
 * @property {string[]} $errors Validation errors array.
 *
 * @property {() => Promise<void>} $save  Persist changes to the collection.
 * @property {() => void} $reset  Discard local modifications.
 * @property {(data:object) => void} $replace  Replace the overlay wholesale.
 * @property {() => Promise<void>} $remove  Delete document from collection.
 */

/**
 * Creates a reactive document instance with custom methods and computed properties.
 * @param {object} collection - The collection wrapper.
 * @param {string|null} documentId - Document ID, or null to create a new unsaved document.
 * @returns {DocumentInstance}
 */
export function useDocument(collection, documentId) {
  if (documentId !== null && (typeof documentId !== 'string' || !documentId)) {
    throw new Error(
      `documentId must be a non-empty string or null (got ${documentId})`
    );
  }
  const collectionName = collection.name;
  const cacheKey = `${collectionName}::${documentId}`;

  const cachedRef = _documentCache.get(cacheKey);
  if (cachedRef) {
    const cached = cachedRef.deref();
    if (cached) return cached;
    _documentCache.delete(cacheKey);
  }

  const documentDef = collection.documentDef || {};
  const schema = collection.schema;
  const propertyKeys = Object.keys(schema.properties);

  // Core state. $raw points to the persisted RxDocument (or a defaults object
  // for new docs). $modified is null until the first local mutation; once
  // created it holds a full reactive deep copy of the document data.
  const $raw = shallowRef(null);
  const $modified = shallowRef(null);
  const $isLoading = ref(documentId !== null);
  const $isNew = ref(documentId === null);
  const saveError = ref(null);

  const jsonOf = raw => {
    if (!raw) return {};
    return typeof raw.toJSON === 'function' ? raw.toJSON() : { ...raw };
  };

  // Use conflictHandler.isEqual when available; fall back to JSON comparison.
  const isEq =
    collection.conflictHandler?.isEqual?.bind(collection.conflictHandler) ??
    ((a, b) => JSON.stringify(a) === JSON.stringify(b));

  // Creates the reactive copy on first touch (write or nested method call).
  // JSON round-trip ensures the underlying object is a fresh mutable plain
  // object — RxDB's toJSON() may return a frozen structure.
  const ensureModified = () => {
    if ($modified.value === null) {
      $modified.value = reactive(JSON.parse(JSON.stringify(jsonOf($raw.value))));
    }
  };

  // Read field value. Always reads $modified.value (even when null) so that
  // effects (templates, computeds) re-run the moment ensureModified fires.
  const readField = key => {
    const mod = $modified.value; // register $modified as a reactive dependency
    if (mod !== null) return mod[key];
    const raw = $raw.value;
    const val = raw ? raw[key] : undefined;
    if (val !== null && typeof val === 'object') {
      return makeActivationProxy(key, []);
    }
    return val;
  };

  const writeField = (key, value) => {
    ensureModified();
    $modified.value[key] = value;
  };

  // Returns a thin proxy over a nested raw value that activates $modified on
  // the first write or method call, then delegates to the reactive copy.
  // getTarget() always reads from the current source ($modified or $raw), so
  // there is no stale reference after activation.
  const makeActivationProxy = (rootKey, subPath) => {
    const getTarget = () => {
      let src =
        $modified.value !== null
          ? $modified.value[rootKey]
          : $raw.value?.[rootKey];
      for (const seg of subPath) src = src?.[seg];
      return src;
    };

    return new Proxy(Object.create(null), {
      get(_, prop) {
        if (typeof prop === 'symbol') return getTarget()?.[prop];
        const target = getTarget();
        const val = target?.[prop];
        if (typeof val === 'function') {
          // Any method call (push, splice, forEach, …) activates $modified so
          // mutations are captured by Vue's reactivity. Read-only calls are
          // harmless since $isModified uses isEq to detect real changes.
          return (...args) => {
            ensureModified();
            return getTarget()[prop](...args);
          };
        }
        if (val !== null && typeof val === 'object') {
          return makeActivationProxy(rootKey, [...subPath, prop]);
        }
        return val;
      },
      set(_, prop, value) {
        ensureModified();
        getTarget()[prop] = value;
        return true;
      },
      has(_, prop) {
        const target = getTarget();
        return target != null && prop in target;
      },
      ownKeys() {
        const target = getTarget();
        return target != null ? Reflect.ownKeys(target) : [];
      },
      getOwnPropertyDescriptor(_, prop) {
        const target = getTarget();
        if (target != null && prop in target) {
          return { value: target[prop], writable: true, enumerable: true, configurable: true };
        }
      },
    });
  };

  const doc = reactive({
    $schema: schema,
    $isLoading,
    $isNew,
  });

  // Schema-driven getter/setter per field.
  for (const key of propertyKeys) {
    Object.defineProperty(doc, key, {
      get() {
        return readField(key);
      },
      set(value) {
        writeField(key, value);
      },
      enumerable: true,
      configurable: true,
    });
  }

  doc.$raw = $raw;

  const $original = computed(() => {
    const raw = $raw.value;
    return raw ? markRaw(jsonOf(raw)) : null;
  });
  doc.$original = $original;

  // JSON round-trip when $modified exists: forces Vue to track all reactive
  // properties deeply (so nested mutations invalidate $data), and returns a
  // plain object safe for AJV validation and collection writes.
  const $data = computed(() => {
    if ($modified.value !== null) {
      return JSON.parse(JSON.stringify($modified.value));
    }
    const result = {};
    for (const key of propertyKeys) {
      const val = $raw.value?.[key];
      if (val !== undefined) result[key] = val;
    }
    return result;
  });
  doc.$data = $data;

  // Semantically true when the current state differs from the persisted state.
  // Editing a field back to its original value returns $isModified to false.
  doc.$isModified = computed(() => {
    if ($modified.value === null) return false;
    return !isEq($data.value, jsonOf($raw.value));
  });

  doc.$errors = computed(() => {
    const saveErrors = saveError.value ? [saveError.value] : [];
    // Skip AJV for persisted unmodified docs — RxDB data is assumed valid.
    if ($modified.value === null && !$isNew.value) return saveErrors;
    const validationErrors = schema
      ? validateSchemaValue($data.value, schema)
      : [];
    return [...validationErrors, ...saveErrors];
  });

  doc.$isValid = computed(() => doc.$errors.length === 0);

  doc.$reset = () => {
    $modified.value = null;
    saveError.value = null;
  };

  doc.$replace = newData => {
    const base = jsonOf($raw.value);
    const merged = { ...base };
    for (const key of propertyKeys) {
      if (key in newData) merged[key] = newData[key];
    }
    $modified.value = reactive(merged);
  };

  doc.$remove = async () => {
    $isLoading.value = true;
    try {
      await collection.remove(documentId);
    } finally {
      $isLoading.value = false;
    }
  };

  doc.$save = async () => {
    saveError.value = null;
    if (!doc.$isValid) {
      throw new Error(`Validation failed: ${JSON.stringify(doc.$errors)}`);
    }
    $isLoading.value = true;
    try {
      const payload = $data.value;
      if ($isNew.value) {
        await collection.insert(payload);
      } else {
        await collection.upsert(payload);
      }
      if (documentId === null) {
        $raw.value = markRaw(createDefaultDocument(schema));
        $modified.value = null;
      } else {
        $modified.value = null;
      }
    } catch (error) {
      saveError.value = error.message ?? 'Unable to save';
    } finally {
      $isLoading.value = false;
    }
  };

  // Internal entry point used by collection.processUpdate to swap in fresh
  // persisted state. When a local overlay exists and a conflictHandler is
  // configured, runs the three-way merge before replacing $raw.
  doc.$$setRaw = rxDoc => {
    if ($modified.value !== null && $raw.value && collection.conflictHandler) {
      const assumedMasterState = jsonOf($raw.value);
      const realMasterState = jsonOf(rxDoc);
      // Plain snapshot of the user's current intent.
      const newDocumentState = JSON.parse(JSON.stringify($modified.value));
      const merged = collection.conflictHandler.resolve({
        newDocumentState,
        realMasterState,
        assumedMasterState,
      });
      if (!isEq(merged, realMasterState)) {
        $modified.value = reactive(merged);
      } else {
        $modified.value = null;
      }
    } else if ($modified.value !== null) {
      // No conflict handler: external update wins, local edits discarded.
      $modified.value = null;
    }
    $raw.value = rxDoc;
    $isLoading.value = false;
  };

  // Custom methods, computed properties, watchers from the collection definition.
  if (documentDef.methods) {
    Object.entries(documentDef.methods).forEach(([name, fn]) => {
      if (name.startsWith('$')) {
        throw new Error(
          `Custom method '${name}' cannot start with '$' (reserved for meta properties)`
        );
      }
      doc[name] = fn.bind(doc);
    });
  }
  if (documentDef.computed) {
    Object.entries(documentDef.computed).forEach(([name, getter]) => {
      if (name.startsWith('$')) {
        throw new Error(
          `Computed property '${name}' cannot start with '$' (reserved for meta properties)`
        );
      }
      doc[name] = computed(() => getter.call(doc));
    });
  }
  if (documentDef.watch) {
    Object.entries(documentDef.watch).forEach(([key, watchDef]) => {
      const handler =
        typeof watchDef === 'function' ? watchDef : watchDef.handler;
      const options = typeof watchDef === 'object' ? watchDef : { deep: false };
      watch(
        () => doc[key],
        (newVal, oldVal) => handler.call(doc, newVal, oldVal),
        options
      );
    });
  }

  // New documents bootstrap $raw from schema defaults so reads work immediately.
  if (documentId === null) {
    $raw.value = markRaw(createDefaultDocument(schema));
    $isLoading.value = false;
  }

  _documentCache.set(cacheKey, new WeakRef(doc));
  return doc;
}

export default useDocument;
